// The rating weights form's check, the API's rule (docs/12 "Usage and ratings"): every kind a
// number from 0 to 1, together summing to 1 within 0.000001. Typed as text, so a French or
// Spanish decimal comma is read too.
import { formatNumber } from "../i18n";
import { RATING_KINDS, type RatingKind, type Weights } from "./model";

export const SUM_TOLERANCE = 0.000001;

export type WeightsInput = Record<RatingKind, string>;

export type WeightsCheck =
  | { ok: true; weights: Weights }
  /** Not a number from 0 to 1. */
  | { ok: false; problem: "weight"; kind: RatingKind }
  | { ok: false; problem: "sum"; sum: number };

function parseWeight(text: string): number | undefined {
  const trimmed = text.trim().replace(",", ".");
  if (!/^(\d+\.?\d*|\.\d+)$/.test(trimmed)) return undefined;
  const w = Number(trimmed);
  return w <= 1 ? w : undefined;
}

export function checkWeights(input: WeightsInput): WeightsCheck {
  const results = parseWeight(input.results);
  if (results === undefined) return { ok: false, problem: "weight", kind: "results" };
  const cost = parseWeight(input.cost);
  if (cost === undefined) return { ok: false, problem: "weight", kind: "cost" };
  const weights: Weights = { results, cost };
  const sum = RATING_KINDS.reduce((s, kind) => s + weights[kind], 0);
  if (Math.abs(sum - 1) > SUM_TOLERANCE) return { ok: false, problem: "sum", sum };
  return { ok: true, weights };
}

/** The weights as the form's text, in the active language ("0.6", "0,6"). */
export const weightsInput = (weights: Weights): WeightsInput => ({
  results: formatNumber(weights.results, { maximumFractionDigits: 6 }),
  cost: formatNumber(weights.cost, { maximumFractionDigits: 6 }),
});
